import { invokeWithRetry } from './common/invokeWithRetry';
import { z } from 'zod';
import { dashboardCancellationSchema, dashboardRatingSchema, dashboardPopularClassesSchema, dashboardBookingsSchema } from '@/services/dashboardResponseSchemas';

const revenueBreakdownsSchema = z.object({ data: z.object({
  bySubscription: z.array(z.object({ name: z.string(), value: z.number() })),
  byDuration: z.array(z.object({ name: z.string(), value: z.number(), count: z.number() })),
  byMethod: z.array(z.object({ name: z.string(), value: z.number(), count: z.number() })),
}) });

/**
 * Plafond de délai propre à `dashboard-kpis`, plus large que les 30 s par
 * défaut de `invokeWithRetry`.
 *
 * Le tableau de bord déclenche ces 11 actions EN PARALLÈLE au chargement, et
 * chacune est une agrégation sur `class_bookings` / `payments` /
 * `subscriptions` bornée par une plage de dates. Sur une grosse salle, la
 * contention entre les 11 requêtes suffit à pousser les plus lourdes au-delà de
 * 30 s. Avec le plafond par défaut, on transformerait un tableau de bord lent
 * mais fonctionnel en tableau de bord en erreur — une régression pure, ces
 * appels n'ayant jamais eu de limite auparavant.
 *
 * La charge n'est pas multipliée pour autant : depuis `retryOnTimeout: false`
 * (défaut), un dépassement de délai n'est plus rejoué. La requête la plus lente
 * part une fois, pas trois.
 */
const DASHBOARD_KPIS_TIMEOUT_MS = 60_000;

const invokeDashboardKpis = (body: Record<string, unknown>) =>
  invokeWithRetry('dashboard-kpis', body, 2, { timeoutMs: DASHBOARD_KPIS_TIMEOUT_MS });

const throwIfAuthExpired = (authExpired: boolean | undefined) => {
  if (authExpired) throw new Error('AUTH_EXPIRED');
};

// ============= Feature Flags =============
const FEATURE_FLAGS = {
  GET_NO_SHOW_STATS: true,
  GET_COACH_RATINGS_STATS: true,
  GET_BOOKING_STATS: true,
  GET_REVENUE_BY_DURATION: true,
  GET_REVENUE_BY_CATEGORY: true,
  GET_RECENT_ACTIVITY: true,
  GET_EXPIRING_SUBSCRIPTIONS: true,
  GET_PENDING_PAYMENTS: true,
  GET_UPCOMING_CLASSES: true,
  GET_RECENT_MEMBERS: true,
  GET_POPULAR_CLASSES: true,
};

// ============= Types =============
export interface BookingStatsResult {
  booking_date: string;
  booking_count: number;
}

/** SQL-aggregated daily receipts: never aggregate a capped payment list. */
export async function getDashboardDailyRevenue(gymId: string, startDate: string, endDate: string): Promise<Array<{ date: string; revenue: number }>> {
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: 'get_growth_data', gym_id: gymId, granularity: 'day',
    start_date: startDate, end_date: endDate,
    comparison_start_date: startDate, comparison_end_date: endDate,
  });
  throwIfAuthExpired(authExpired);
  if (error) throw error;
  if (!Array.isArray(data?.data)) throw new Error('Invalid dashboard revenue response');
  return data.data.map((point: { month?: unknown; revenue?: unknown }) => {
    if (typeof point.month !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(point.month)
      || point.month < startDate || point.month > endDate || point.revenue === null || point.revenue === undefined
      || !Number.isFinite(Number(point.revenue))) throw new Error('Invalid dashboard revenue point');
    return { date: point.month, revenue: Number(point.revenue) };
  });
}

export interface RevenueByDurationResult {
  name: string;
  value: number;
  count: number;
}

export interface RevenueByCategoryResult {
  name: string;
  value: number;
}

export interface ExpiringSubscription {
  id: string;
  end_date: string;
  status: string;
  type: string;
  user_profiles: { id: string; first_name: string; last_name: string } | null;
}

export interface PendingPayment {
  id: string;
  amount: number;
  payment_date: string;
  status: string;
  user_profiles: { id: string; first_name: string; last_name: string } | null;
}

export interface UpcomingClass {
  id: string;
  name: string;
  time: string;
  instructor: string;
  capacity: number;
  bookedCount: number;
  spotsLeft: number;
}

export interface RecentMember {
  id: string;
  name: string;
  joinedAt: string;
}

export interface PopularClass {
  class_name: string;
  booking_count: number;
}

export interface DashboardRevenueBreakdowns {
  bySubscription: RevenueByCategoryResult[];
  byDuration: RevenueByDurationResult[];
  byMethod: Array<{ name: string; value: number; count: number }>;
}

// ============= Edge Function Wrappers =============

export async function getNoShowStats(
  gymId: string,
  startDate: string,
  endDate: string
) {
  if (!FEATURE_FLAGS.GET_NO_SHOW_STATS) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getNoShowStats");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getNoShowStats");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_no_show_stats",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);

  if (error) throw error;
  return dashboardCancellationSchema.parse(data).data as Array<{class_name:string;total_bookings:number;cancelled_bookings:number;no_show_rate:number}>;
}

export async function getCoachRatingsStats(
  gymId: string,
  startDate?: string,
  endDate?: string
) {
  if (!FEATURE_FLAGS.GET_COACH_RATINGS_STATS) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getCoachRatingsStats");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getCoachRatingsStats");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_coach_ratings_stats",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);

  if (error) throw error;
  return dashboardRatingSchema.parse(data).data as Array<{instructor:string;month:string;avg_rating:number;total_ratings:number}>;
}

export async function getBookingStats(
  gymId: string,
  startDate: string,
  endDate: string
): Promise<BookingStatsResult[]> {
  return (await getBookingStatsWithScope(gymId,startDate,endDate)).data;
}

export async function getBookingStatsWithScope(gymId: string, startDate: string, endDate: string): Promise<{ data: BookingStatsResult[]; bookingScope?: 'calendar' }> {
  if (!FEATURE_FLAGS.GET_BOOKING_STATS) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getBookingStats");
    return { data: [], bookingScope: undefined };
  }

  console.log("[dashboardStatsWrapper] Using edge function for getBookingStats");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_booking_stats",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);

  if (error) throw error;
  const parsed = dashboardBookingsSchema.parse(data);
  if (parsed.data.some((point) => point.booking_date < startDate || point.booking_date > endDate || point.booking_count < 0)) {
    throw new Error('Invalid dashboard booking range or count');
  }
  return { data: parsed.data.map(point => ({ booking_date: String(point.booking_date), booking_count: Number(point.booking_count) })), bookingScope: parsed.bookingScope };
}

export async function getRevenueByDuration(
  gymId: string,
  startDate: string,
  endDate: string,
): Promise<RevenueByDurationResult[]> {
  if (!FEATURE_FLAGS.GET_REVENUE_BY_DURATION) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getRevenueByDuration");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getRevenueByDuration");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_revenue_by_duration",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);

  if (error) throw error;
  return data.data;
}

export async function getRevenueByCategory(
  gymId: string,
  startDate: string,
  endDate: string,
): Promise<RevenueByCategoryResult[]> {
  if (!FEATURE_FLAGS.GET_REVENUE_BY_CATEGORY) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getRevenueByCategory");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getRevenueByCategory");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_revenue_by_category",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);

  if (error) throw error;
  return data.data;
}

export async function getDashboardRevenueBreakdowns(
  gymId: string,
  startDate: string,
  endDate: string,
): Promise<DashboardRevenueBreakdowns> {
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_revenue_breakdowns",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);
  if (error) throw error;
  return revenueBreakdownsSchema.parse(data).data as DashboardRevenueBreakdowns;
}

export async function getRecentActivity(gymId: string, startDate: string, endDate: string) {
  if (!FEATURE_FLAGS.GET_RECENT_ACTIVITY) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getRecentActivity");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getRecentActivity");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_recent_activity",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);

  if (error) throw error;
  return data.data;
}

// ============= NEW WIDGET FUNCTIONS =============

export async function getExpiringSubscriptions(
  gymId: string
): Promise<ExpiringSubscription[]> {
  if (!FEATURE_FLAGS.GET_EXPIRING_SUBSCRIPTIONS) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getExpiringSubscriptions");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getExpiringSubscriptions");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_expiring_subscriptions",
    gym_id: gymId,
  });

  throwIfAuthExpired(authExpired);
  if (error) throw error;
  return data.data;
}

export async function getPendingPayments(
  gymId: string
): Promise<PendingPayment[]> {
  if (!FEATURE_FLAGS.GET_PENDING_PAYMENTS) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getPendingPayments");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getPendingPayments");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_pending_payments",
    gym_id: gymId,
  });

  throwIfAuthExpired(authExpired);
  if (error) throw error;
  return data.data;
}

export async function getUpcomingClasses(
  gymId: string
): Promise<UpcomingClass[]> {
  if (!FEATURE_FLAGS.GET_UPCOMING_CLASSES) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getUpcomingClasses");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getUpcomingClasses");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_upcoming_classes",
    gym_id: gymId,
  });

  throwIfAuthExpired(authExpired);
  if (error) throw error;
  return data.data;
}

export async function getRecentMembers(
  gymId: string
): Promise<RecentMember[]> {
  if (!FEATURE_FLAGS.GET_RECENT_MEMBERS) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getRecentMembers");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getRecentMembers");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_recent_members",
    gym_id: gymId,
  });

  throwIfAuthExpired(authExpired);
  if (error) throw error;
  return data.data;
}

export async function getPopularClasses(
  gymId: string,
  startDate: string,
  endDate: string
): Promise<PopularClass[]> {
  if (!FEATURE_FLAGS.GET_POPULAR_CLASSES) {
    console.log("[dashboardStatsWrapper] Feature flag disabled for getPopularClasses");
    return [];
  }

  console.log("[dashboardStatsWrapper] Using edge function for getPopularClasses");
  const { data, error, authExpired } = await invokeDashboardKpis({
    action: "get_popular_classes",
    gym_id: gymId,
    start_date: startDate,
    end_date: endDate,
  });

  throwIfAuthExpired(authExpired);
  if (error) throw error;
  return dashboardPopularClassesSchema.parse(data).data as PopularClass[];
}
